/**
 * ACE ビュー用の戦闘力テーブル群 (4 チーム別)。
 *
 * ACE の戦闘力サマリーを airCloset / Mall / Platform / QA の 4 チームに分け、
 * それぞれ各チームの代表色の見出しで表示する。メンバー分類は AceTeamsChart と同一
 * (`memberTeamMap` の group=ACE かつ team 一致) で、特例 (taro 等) は 4 チームの
 * いずれにも属さないため表には出さない (既存 AceTeamsChart と同挙動)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ACE 戦闘力サマリーをサブチーム別 (代表色見出し) に表示
 * @graph-connects none
 */

import { useTranslation } from 'react-i18next';
import type { CombatPowerResponse } from '../../../types/combat-power.js';
import { TeamTable } from './TeamTable.js';
import { QuarterTeamTable } from './QuarterTeamTable.js';
import { ACE_SUB_TEAMS } from './AceTeamsChart.js';

/**
 * ACE 各サブチームの代表色 (6 桁 HEX)。
 * airCloset=赤 / Mall=緑 / Platform=青 / QA=黄。
 * @graph-connects none
 */
const ACE_TEAM_COLORS: Record<(typeof ACE_SUB_TEAMS)[number]['key'], string> = {
  aircloset: '#ef4444',
  mall: '#22c55e',
  platform: '#3b82f6',
  qa: '#eab308',
};

interface AceTeamTablesProps {
  data: CombatPowerResponse;
  memberTeamMap: Map<string, { group: string; team: string }>;
  currentMonthIdx: number;
  /** 担当者名 (小文字) → email。指定時は担当者名を詳細ページリンクにする */
  nameToEmail?: Map<string, string>;
  /** true なら各チームを四半期ビュー (月別の期待値/実績 横並び) で表示する */
  quarterView?: boolean;
}

/** @graph-connects none */
export function AceTeamTables({
  data,
  memberTeamMap,
  currentMonthIdx,
  nameToEmail,
  quarterView,
}: AceTeamTablesProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-8 gap-y-6">
      {ACE_SUB_TEAMS.map((st) => {
        const members = data.members.filter((m) => {
          const info = memberTeamMap.get(m.name.toLowerCase());
          return info?.group === 'ACE' && info.team === st.team;
        });
        return quarterView ? (
          <QuarterTeamTable
            key={st.key}
            teamLabel={st.label}
            members={members}
            monthHeaders={data.monthHeaders}
            nameToEmail={nameToEmail}
            accentColor={ACE_TEAM_COLORS[st.key]}
            emptyMessage={t('members.noMembers')}
          />
        ) : (
          <TeamTable
            key={st.key}
            teamLabel={st.label}
            members={members}
            currentMonthIdx={currentMonthIdx}
            nameToEmail={nameToEmail}
            accentColor={ACE_TEAM_COLORS[st.key]}
            emptyMessage={t('members.noMembers')}
          />
        );
      })}
    </div>
  );
}
